import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useRef } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import type { RecentlyResolvedIncident } from "@/client/incident";
import { DEFAULT_RESOLUTION_MESSAGE } from "@/utils/incident/resolution";
import styles from "./styles.module.css";
type Props = {
  incidents: RecentlyResolvedIncident[];
  onDismiss: (id: string) => void;
};

/** 復旧バナーの実描画高さを export する CSS 変数。globals.css の --top-stack-height が合算する */
const RESOLVED_BAR_HEIGHT_CSS_VAR = "--incident-resolved-height";

/**
 * 直前まで active だった incident が resolved に遷移したときの復旧バナー。
 * 一覧で表示し、ユーザの dismiss 操作で 1 件ずつ閉じられる。
 *
 * sticky で flow に高さを占有し下のコンテンツを押し下げるため、実描画高さを CSS 変数
 * `--incident-resolved-height` として :root に export する。100vh 基準で高さを取る画面
 * (ホーム・サイドバー等) がその分を差し引いてはみ出しを防ぐ。アンマウント時は 0 に戻す。
 */
export const IncidentResolvedBanner: React.FC<Props> = t0 => {
  const $ = _c(9);
  const {
    incidents,
    onDismiss
  } = t0;
  const ref = useRef(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const root = document.documentElement;
      const el = ref.current;
      if (!el) {
        return;
      }
      const update = () => {
        root.style.setProperty(RESOLVED_BAR_HEIGHT_CSS_VAR, `${el.offsetHeight}px`);
      };
      update();
      const observer = new ResizeObserver(update);
      observer.observe(el);
      return () => {
        observer.disconnect();
        root.style.setProperty(RESOLVED_BAR_HEIGHT_CSS_VAR, "0px");
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useLayoutEffect(t1, t2);
  let t3;
  if ($[2] !== incidents || $[3] !== onDismiss) {
    let t4;
    if ($[5] !== onDismiss) {
      t4 = incident => <Box key={incident.id} className={styles.item}><CheckCircleOutlineIcon className={styles.icon} /><Typography className={styles.message}>{incident.resolutionMessage ?? DEFAULT_RESOLUTION_MESSAGE}</Typography><IconButton aria-label={"\u5FA9\u65E7\u30D0\u30CA\u30FC\u3092\u9589\u3058\u308B"} className={styles.close} onClick={() => onDismiss(incident.id)}><CloseIcon /></IconButton></Box>;
      $[5] = onDismiss;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t3 = incidents.map(t4);
    $[2] = incidents;
    $[3] = onDismiss;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <Box ref={ref} className={styles.root} role="status" aria-live="polite">{t3}</Box>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};